import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import apiClient from '../lib/apiClient';

const AuthContext = createContext(null);

const TOKEN_EXP_KEY = 'xhotel_token_exp';
const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

const isTokenExpired = (token) => {
  try {
    // Standard JWS: decode payload directly
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Date.now() >= payload.exp * 1000;
  } catch {
    // Encrypted JWE: payload is not readable — fall back to stored expiry
    const exp = localStorage.getItem(TOKEN_EXP_KEY);
    return !exp || Date.now() >= Number(exp);
  }
};

const getValidToken = () => {
  const t = localStorage.getItem('xhotel_token');
  if (!t || isTokenExpired(t)) {
    localStorage.removeItem('xhotel_token');
    localStorage.removeItem('xhotel_user');
    localStorage.removeItem(TOKEN_EXP_KEY);
    return null;
  }
  return t;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => getValidToken());
  const [user, setUser] = useState(() => {
    if (!getValidToken()) return null;
    try { return JSON.parse(localStorage.getItem('xhotel_user')); } catch { return null; }
  });

  // Sync auth state when localStorage changes in another tab
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'xhotel_token') {
        setToken(e.newValue || null);
        if (!e.newValue) setUser(null);
      }
      if (e.key === 'xhotel_user') {
        try { setUser(e.newValue ? JSON.parse(e.newValue) : null); } catch { setUser(null); }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const login = useCallback(async (userId, password) => {
    const data = await apiClient.post('/api/User/GuestLogin', { userId, password });
    localStorage.setItem('xhotel_token', data.token);
    localStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + TWO_HOURS_MS));
    const userInfo = { userId, name: data.userName || userId, role: data.role };
    localStorage.setItem('xhotel_user', JSON.stringify(userInfo));
    setToken(data.token);
    setUser(userInfo);
    return data;
  }, []);

  const register = useCallback(async (form) => {
    return await apiClient.post('/api/User/AddGuest', { ...form, role: 5 });
  }, []);

  const setPassword = useCallback(async (email, otp, newPassword, firstName, lastName, phone) => {
    return await apiClient.post('/api/User/SetPassword', { email, otp, newPassword, firstName, lastName, phone });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('xhotel_token');
    localStorage.removeItem('xhotel_user');
    localStorage.removeItem(TOKEN_EXP_KEY);
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ token, user, isLoggedIn: !!token, login, logout, register, setPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
